import { getChatFriendDetections } from '@/api/modules/chat'
import { syncCustomerServiceWechatContacts } from '@/api/modules/customerServiceWechat'
import { ipcCall } from '@/composables/useIPC'
import { IpcCommand, type EndpointProfileInfo } from '@/types/ipc'

const cacheKeyPrefix = 'customer-service-wechat:friend-detection'
const detectionBatchSize = 200

interface CustomerFriendDetectionCacheIdentity {
  companyId: number
  userId: number
}

interface CustomerFriendDetectionSyncOptions extends CustomerFriendDetectionCacheIdentity {
  accountId: number
  ownerWxId: string
  today?: string
}

interface CustomerFriendDetectionSyncResult {
  received: number
  updated: number
  unmatched: number
}

function accountCacheKey(scope: string, accountId: number, suffix: 'cursor' | 'pending') {
  return `${cacheKeyPrefix}:${scope}:account:${accountId}:${suffix}`
}

function accessDateCacheKey(scope: string) {
  return `${cacheKeyPrefix}:${scope}:access-date`
}

function isDateText(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

function shanghaiDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return `${values.year}-${values.month}-${values.day}`
}

function addDays(date: string, days: number) {
  const value = new Date(`${date}T00:00:00Z`)
  value.setUTCDate(value.getUTCDate() + days)
  return value.toISOString().slice(0, 10)
}

function queryDates(accessDate: string, cursor: string | null, today: string) {
  let start = cursor && isDateText(cursor) ? cursor : accessDate
  if (start < accessDate) start = accessDate
  if (start > today) start = today
  const dates: string[] = []
  for (let date = start; date <= today; date = addDays(date, 1)) dates.push(date)
  return dates
}

async function readCache(key: string) {
  return ipcCall<string | null>(IpcCommand.CacheGet, { key })
}

async function writeCache(key: string, value: string) {
  await ipcCall(IpcCommand.CacheSet, { key, value, ttl: 0 })
}

function positiveId(value: number) {
  return Number.isInteger(value) && value > 0 ? value : 0
}

async function resolveCacheScope(identity: CustomerFriendDetectionCacheIdentity) {
  const companyId = positiveId(identity.companyId)
  const userId = positiveId(identity.userId)
  if (!companyId || !userId) throw new Error('好友首次检测缓存缺少当前公司或登录用户')

  const [profile, apiBase] = await Promise.all([
    ipcCall<EndpointProfileInfo>(IpcCommand.EndpointProfileInfo),
    ipcCall<string>(IpcCommand.ApiBaseUrl),
  ])
  const endpoint = (profile.apiBase || apiBase || '').trim()
  if (!endpoint) throw new Error('好友首次检测缓存缺少当前服务环境')
  const profileIdentity =
    profile.present && profile.valid
      ? `profile:${profile.companyCode?.trim().toLowerCase() || 'unknown'}:${endpoint}`
      : `public:${endpoint}`
  return `endpoint:${encodeURIComponent(profileIdentity)}:company:${companyId}:user:${userId}`
}

async function initializeAccessDate(scope: string, today: string) {
  const key = accessDateCacheKey(scope)
  const cached = await readCache(key)
  if (isDateText(cached)) return cached
  const accessDate = isDateText(today) ? today : shanghaiDate()
  await writeCache(key, accessDate)
  return accessDate
}

export async function initializeCustomerFriendDetectionAccessDate(
  identity: CustomerFriendDetectionCacheIdentity,
  today = shanghaiDate(),
) {
  return initializeAccessDate(await resolveCacheScope(identity), today)
}

function readPending(value: string | null): CustomerServiceWechatFriendDetection[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is CustomerServiceWechatFriendDetection =>
        item &&
        typeof item.wxId === 'string' &&
        item.wxId.trim() !== '' &&
        typeof item.alias === 'string' &&
        isDateText(item.detectedDate),
    )
  } catch {
    return []
  }
}

function normalizeDetections(
  detections: Array<Omit<CustomerServiceWechatFriendDetection, 'sourceIndex'>>,
) {
  const seen = new Set<string>()
  const result: CustomerServiceWechatFriendDetection[] = []
  for (const detection of detections) {
    const wxId = detection.wxId.trim()
    const key = `${detection.detectedDate}:${wxId}`
    if (!wxId || !isDateText(detection.detectedDate) || seen.has(key)) continue
    seen.add(key)
    result.push({
      sourceIndex: result.length,
      wxId,
      alias: detection.alias.trim(),
      detectedDate: detection.detectedDate,
    })
  }
  return result
}

export async function syncCustomerFriendDetections(
  options: CustomerFriendDetectionSyncOptions,
): Promise<CustomerFriendDetectionSyncResult> {
  if (options.accountId <= 0 || !options.ownerWxId.trim()) {
    throw new Error('好友首次检测同步参数不完整')
  }
  const today = isDateText(options.today) ? options.today : shanghaiDate()
  const cacheScope = await resolveCacheScope(options)
  const accessDate = await initializeAccessDate(cacheScope, today)
  const cursorKey = accountCacheKey(cacheScope, options.accountId, 'cursor')
  const pendingKey = accountCacheKey(cacheScope, options.accountId, 'pending')
  const [cursor, pendingValue] = await Promise.all([readCache(cursorKey), readCache(pendingKey)])
  const fetched: Array<Omit<CustomerServiceWechatFriendDetection, 'sourceIndex'>> = []
  for (const date of queryDates(accessDate, cursor, today)) {
    const response = await getChatFriendDetections(options.ownerWxId.trim(), date)
    fetched.push(...response.contacts)
  }
  const pending = readPending(pendingValue)
  const allDetections = normalizeDetections([...pending, ...fetched])

  // 先持久化完整待处理集合；后端批次中断时可在下次手动同步中幂等重试。
  await writeCache(pendingKey, JSON.stringify(allDetections))
  const unmatched: CustomerServiceWechatFriendDetection[] = []
  let updated = 0
  for (let offset = 0; offset < allDetections.length; offset += detectionBatchSize) {
    const batch = allDetections.slice(offset, offset + detectionBatchSize)
    const response = await syncCustomerServiceWechatContacts(options.accountId, [], batch)
    updated += response.friendDetectionUpdated ?? 0
    if (!Array.isArray(response.friendDetectionUnmatched)) {
      throw new Error('当前后台版本不支持好友首次检测同步')
    }
    unmatched.push(...response.friendDetectionUnmatched)
  }
  const remaining = normalizeDetections(unmatched)
  await writeCache(pendingKey, JSON.stringify(remaining))
  await writeCache(cursorKey, today)
  return { received: allDetections.length, updated, unmatched: remaining.length }
}
